const pad = (value) => String(value).padStart(2, "0");

const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const toOptionalNumber = (value) => {
    if (value === undefined || value === null || value === "") {
        return undefined;
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
};

const pickFirstValue = (...values) =>
    values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeRoomType = (value) => {
    const roomType = String(value || "").toLowerCase();
    if (roomType.includes("small")) return "small";
    if (roomType.includes("medium")) return "medium";
    if (roomType.includes("large")) return "large";
    return roomType || "";
};

const formatTime = (seconds) => {
    const timestamp = toOptionalNumber(seconds);
    if (timestamp === undefined) {
        return "";
    }

    const date = new Date(timestamp * 1000);
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getBranchId = (slot = {}) =>
    pickFirstValue(slot.branch_id, slot.branch, slot.room_id);

const findBranch = (branches = [], branchId) =>
    branches.find((branch) => branch?._id === branchId);

const getRoomConfig = (branch = {}, roomType) => branch?.[roomType] || {};

const getPriceValue = (slot = {}) =>
    pickFirstValue(slot.priceOrder, slot.price_order, slot.fee, slot.price);

const getDiscountedPriceValue = (slot = {}) =>
    pickFirstValue(
        slot.priceOrderDiscount,
        slot.price_discounted,
        slot.discounted_fee,
        slot.discounted_price,
    );

const getNextPriceBoundary = (date) => {
    const boundaries = [6, 18]
        .map((hour) => {
            const boundary = new Date(date.getTime());
            boundary.setHours(hour, 0, 0, 0);
            if (date.getTime() >= boundary.getTime()) {
                boundary.setDate(boundary.getDate() + 1);
            }
            return boundary;
        })
        .sort((left, right) => left.getTime() - right.getTime());

    return boundaries[0];
};

const isNightTime = (date) => {
    const hour = date.getHours();
    return hour >= 18 || hour < 6;
};

const hasPriceConfig = (roomConfig = {}) =>
    roomConfig?.day_price !== undefined ||
    roomConfig?.night_price !== undefined;

const getSegmentPrice = (date, roomConfig = {}) => {
    if (isNightTime(date)) {
        return toNumber(roomConfig?.night_price);
    }
    return toNumber(roomConfig?.day_price);
};

export const calculateSuggestionPrice = (startTime, endTime, roomConfig = {}) => {
    const startSeconds = toOptionalNumber(startTime);
    const endSeconds = toOptionalNumber(endTime);

    if (startSeconds === undefined || endSeconds === undefined) {
        return 0;
    }

    const start = new Date(startSeconds * 1000);
    const end = new Date(endSeconds * 1000);

    if (
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime()) ||
        end.getTime() <= start.getTime() ||
        !hasPriceConfig(roomConfig)
    ) {
        return 0;
    }

    let cursor = start;
    let total = 0;

    while (cursor.getTime() < end.getTime()) {
        const nextBoundary = getNextPriceBoundary(cursor);
        const segmentEnd =
            nextBoundary.getTime() < end.getTime() ? nextBoundary : end;
        const hours = (segmentEnd.getTime() - cursor.getTime()) / 3600000;
        total += hours * getSegmentPrice(cursor, roomConfig);
        cursor = segmentEnd;
    }

    return total;
};

const normalizeSlot = (
    slot = {},
    branches = [],
    requestedBooking = {},
    suggestionGroup,
    suggestionIndex,
) => {
    const roomType = normalizeRoomType(slot.room_type || slot.room || requestedBooking.room_type);
    const branchId = getBranchId(slot);
    const branch = findBranch(branches, branchId) || {};
    const roomConfig = getRoomConfig(branch, roomType);
    const startTime = toOptionalNumber(slot.start_time);
    const explicitEndTime = toOptionalNumber(slot.end_time);
    const explicitDuration = toOptionalNumber(pickFirstValue(slot.duration_minutes, slot.time_box));
    const durationMinutesValue =
        explicitDuration ??
        (startTime !== undefined && explicitEndTime !== undefined
            ? (explicitEndTime - startTime) / 60
            : undefined);
    const endTime =
        explicitEndTime ??
        (startTime !== undefined && durationMinutesValue !== undefined
            ? startTime + durationMinutesValue * 60
            : undefined);
    const backendPrice = getPriceValue(slot);
    const priceOrder =
        backendPrice !== undefined
            ? toNumber(backendPrice)
            : calculateSuggestionPrice(startTime, endTime, roomConfig);
    const backendDiscountedPrice = getDiscountedPriceValue(slot);
    const priceOrderDiscount =
        backendDiscountedPrice !== undefined ? toNumber(backendDiscountedPrice) : priceOrder;
    const roomName = pickFirstValue(slot.room_name, slot.name, branch?.name, "");

    return {
        ...slot,
        id: pickFirstValue(slot.id, slot._id, `${suggestionGroup}-${suggestionIndex}`),
        _id: branchId,
        branch_id: branchId,
        branch: branchId,
        branch_name: branch?.name || slot.branch_name || "",
        name: branch?.name || slot.name || roomName,
        address: branch?.address || slot.address || "",
        google_map: branch?.google_map || slot.google_map || "",
        google_map_direct_link:
            branch?.google_map_direct_link || slot.google_map_direct_link || "",
        room: roomType,
        room_type: roomType,
        room_name: roomName,
        capacity: toNumber(slot.capacity ?? roomConfig?.capacity, 4),
        day_price: toNumber(slot.day_price ?? roomConfig?.day_price),
        night_price: toNumber(slot.night_price ?? roomConfig?.night_price),
        start_time: startTime,
        end_time: endTime,
        start_time_formatted: slot.start_time_formatted || formatTime(startTime),
        end_time_formatted: slot.end_time_formatted || formatTime(endTime),
        duration_minutes: toNumber(durationMinutesValue),
        time_box: toNumber(durationMinutesValue),
        duration_description: slot.duration_description || "",
        duration_type: slot.duration_type || "",
        isExactDuration: slot.duration_type === "exact",
        priceOrder,
        priceOrderDiscount,
        suggestion_group: suggestionGroup,
        suggestion_index: suggestionIndex,
        isBookable: isSuggestionBookable({
            branch_id: branchId,
            room_type: roomType,
            start_time: startTime,
            duration_minutes: durationMinutesValue,
        }),
    };
};

export const normalizeSuggestionResponse = (responseData = {}, branches = []) => {
    const suggestions = responseData?.suggestions || {};
    const requestedBooking = responseData?.requested_booking || {};
    const sameTypeSlots = (suggestions?.same_type?.slots || []).map((slot, index) =>
        normalizeSlot(slot, branches, requestedBooking, "same_type", index),
    );
    const otherTypeSlots = (suggestions?.other_types?.slots || []).map((slot, index) =>
        normalizeSlot(slot, branches, requestedBooking, "other_type", index),
    );
    const allSlots = [...sameTypeSlots, ...otherTypeSlots];

    return {
        sameTypeSlots,
        otherTypeSlots,
        allSlots,
        totalCount: toNumber(suggestions?.total_count, allSlots.length),
        requestedBooking,
    };
};

export const buildSuggestionCheckRoomPayload = (slot = {}) => ({
    branch: slot.branch_id,
    room: slot.room_type,
    time_box: slot.duration_minutes,
    start_time: slot.start_time,
});

export const isSuggestionBookable = (slot = {}) =>
    Boolean(
        slot.branch_id &&
            slot.room_type &&
            toOptionalNumber(slot.start_time) !== undefined &&
            toNumber(slot.duration_minutes) > 0,
    );

export const buildSuggestionFormSearch = (slot = {}, payload = {}) => ({
    ...payload,
    priceOrder: slot.priceOrder,
    priceOrderDiscount: slot.priceOrderDiscount,
    discountDetail: slot.discountDetail || slot.discount_detail || null,
    time: null,
    start_time: slot.start_time,
    end_time: slot.end_time,
});
